import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Button from './Button';
import adminStore from '../Stores/AdminStore';

const UserMergeModal = ({
  isOpen,
  onClose,
  sourceUser,
  users,
  onMergeComplete
}) => {
  const [targetUserId, setTargetUserId] = useState('');
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setTargetUserId('');
      setPreview(null);
      setConfirmation('');
      setError(null);
      setIsLoading(false);
    }
  }, [isOpen]);

  // Auto-fetch preview when target user is selected
  useEffect(() => {
    if (targetUserId && sourceUser) {
      setIsFetchingPreview(true);
      setError(null);
      adminStore
        .previewUserMerge(sourceUser.id, parseInt(targetUserId))
        .then((result) => {
          setPreview(result.preview);
          setIsFetchingPreview(false);
        })
        .catch((err) => {
          setError(err.response?.data?.message || 'Failed to load preview');
          setIsFetchingPreview(false);
        });
    } else {
      setPreview(null);
    }
  }, [targetUserId, sourceUser]);

  const handleMerge = async () => {
    if (confirmation !== sourceUser.username) {
      setError('Username confirmation does not match');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await adminStore.mergeUsers(sourceUser.id, parseInt(targetUserId));
      onMergeComplete(result);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to merge users');
      setIsLoading(false);
    }
  };

  // Filter out source user from target selection
  const availableTargets = (users || []).filter(u => u.id !== sourceUser?.id);

  const isConfirmationValid = confirmation === sourceUser?.username;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Merge User Accounts"
      size="lg"
    >
      <div className="space-y-6">
        {/* Error Display */}
        {error && (
          <div className="bg-red-900 p-3 rounded-lg">
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}

        {/* Source User (Read-only) */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Source User (will be deleted)
          </label>
          <div className="p-3 bg-secondary rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">{sourceUser?.username}</div>
                <div className="text-sm opacity-70">{sourceUser?.email}</div>
              </div>
              <div className="px-2 py-1 bg-red-500 bg-opacity-20 text-red-400 rounded text-xs font-medium">
                Will be deleted
              </div>
            </div>
          </div>
        </div>

        {/* Target User Selection */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Target User (will receive all data)
          </label>
          <select
            value={targetUserId}
            onChange={(e) => setTargetUserId(e.target.value)}
            disabled={isLoading}
            className="
              w-full p-3 rounded-lg bg-secondary text-text border-2 border-transparent focus:border-accent focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            <option value="">Select a user...</option>
            {availableTargets.map((user) => (
              <option key={user.id} value={user.id}>
                {user.username} ({user.email})
              </option>
            ))}
          </select>
        </div>

        {/* Preview Loading */}
        {isFetchingPreview && (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-accent" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        )}

        {/* Preview Counts */}
        {preview && !isFetchingPreview && (
          <div>
            <h3 className="text-sm font-medium mb-3">Data to be transferred:</h3>
            <div className="bg-secondary rounded-lg p-4">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-500">
                  <tr>
                    <td className="py-2 font-medium">Songs</td>
                    <td className="py-2 text-right">{preview.songs}</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium">Play History</td>
                    <td className="py-2 text-right">{preview.plays}</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium">Playlists</td>
                    <td className="py-2 text-right">{preview.playlists}</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium">Follows</td>
                    <td className="py-2 text-right">{preview.follows}</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium">Song Filters</td>
                    <td className="py-2 text-right">{preview.songFilters}</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium">Playlist Shares</td>
                    <td className="py-2 text-right">{preview.playlistShares}</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium">Push Subscriptions</td>
                    <td className="py-2 text-right">{preview.pushSubscriptions}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Warning about invites */}
            <div className="mt-4 p-3 bg-yellow-500 bg-opacity-10 border-l-4 border-yellow-500 rounded">
              <p className="text-sm text-yellow-400">
                <strong>Note:</strong> User invites will be deleted and not transferred.
              </p>
            </div>
          </div>
        )}

        {/* Confirmation Input */}
        {preview && !isFetchingPreview && (
          <div>
            <label className="block text-sm font-medium mb-2">
              Type <span className="font-mono bg-secondary px-1 rounded">{sourceUser?.username}</span> to confirm
            </label>
            <input
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              disabled={isLoading}
              placeholder={`Type "${sourceUser?.username}" to confirm`}
              className="
                w-full p-3 rounded-lg bg-secondary text-text border-2 border-transparent focus:border-accent focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed
              "
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-secondary">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleMerge}
            disabled={!isConfirmationValid || isLoading || !preview}
            className="flex-1"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Merging...
              </span>
            ) : (
              'Merge Users'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UserMergeModal;
